import type {Notification} from "@juis/commons";
import {Component, type ComponentEvents, Container, ON_POP_STATE} from "@juis/components";
import {type CompiledPattern, compilePattern} from "./compilePattern.js";

export interface RouteContext {
    readonly url: string;
    readonly params: Readonly<Record<string, string>>;
    readonly query: URLSearchParams;
    /** Path left over after this route's pattern, for nested routers. */
    readonly remaining: string;
}

export type RouteFactory = (context: RouteContext) => Component<ComponentEvents> | Promise<Component<ComponentEvents>>;

export interface RouteOptions {
    /** Rebuild the view on every visit instead of reusing the cached instance. */
    alwaysReload?: boolean;
}

export interface RouterEvents extends ComponentEvents {
    beforeNavigation: Notification<{ url: string }>;
    navigate: Notification<RouteContext>;
    afterNavigation: Notification<RouteContext>;
    navigationError: Notification<{ url: string; error: unknown }>;
}

export interface RouterPages {
    loading?: () => Component<ComponentEvents>;
    notFound?: () => Component<ComponentEvents>;
    error?: (error: unknown) => Component<ComponentEvents>;
}

/** A view that owns a router of its own and continues where the parent route stopped. */
export interface NestedRoutable {
    navigateNested(remaining: string): void | Promise<void>;
}

const isNestedRoutable = (view: object): view is NestedRoutable =>
    typeof (view as Partial<NestedRoutable>).navigateNested === "function";

export interface RouterOptions extends RouterPages {
    /** Path used when nothing matches and no not-found page is configured. */
    fallbackUrl?: string;
    /** Prefix this router is mounted under, stripped before matching. */
    basePath?: string;
}

interface Route {
    readonly pattern: CompiledPattern;
    readonly factory: RouteFactory;
    readonly alwaysReload: boolean;
}

/**
 * Renders one view at a time and keeps the URL in sync. Views may be returned directly or
 * imported lazily by the factory, so route splitting needs no framework support.
 */
export class Router extends Container<RouterEvents> {
    readonly #routes: Route[] = [];
    readonly #cache = new Map<string, Component<ComponentEvents>>();
    readonly #options: RouterOptions;
    #current: Component<ComponentEvents> | undefined;
    #navigationId = 0;
    #started = false;

    constructor(options: RouterOptions = {}) {
        super({cssClasses: ["router"]});
        this.#options = options;
        this.on("requestNavigate", (request, event) => {
            if (!this.#canHandle(request.url)) {
                return;
            }
            event.stopPropagation();
            void this.navigateTo(request.url, {replace: request.replace});
        });
    }

    addRoute(pattern: string | RegExp, factory: RouteFactory, options: RouteOptions = {}): this {
        this.#routes.push({
            pattern: compilePattern(pattern),
            factory,
            alwaysReload: options.alwaysReload ?? false
        });
        return this;
    }

    /** Renders the current location and follows browser history from then on. */
    async start(): Promise<void> {
        if (!this.#started) {
            this.#started = true;
            window.addEventListener(ON_POP_STATE, () => {
                void this.#render(window.location.pathname + window.location.search);
            });
        }
        await this.#render(window.location.pathname + window.location.search);
    }

    async navigateTo(url: string, options: { replace?: boolean } = {}): Promise<void> {
        if (options.replace) {
            window.history.replaceState({}, "", url);
        } else {
            window.history.pushState({}, "", url);
        }
        await this.#render(url);
    }

    /** Renders the part of the path a parent router left over. */
    async navigateNested(remaining: string): Promise<void> {
        await this.#render(remaining === "" ? "/" : remaining);
    }

    /** A request is ours when it resolves here; anything else belongs to a router further up. */
    #canHandle(url: string): boolean {
        const [path = "", search = ""] = splitUrl(url);
        const basePath = this.#options.basePath;
        if (basePath !== undefined && basePath !== "" && !path.startsWith(basePath)) {
            return false;
        }
        return this.#resolve(this.#stripBasePath(path), search) !== undefined;
    }

    async #render(url: string): Promise<void> {
        const navigationId = ++this.#navigationId;
        this.trigger("beforeNavigation", {url});

        const [path = "", search = ""] = splitUrl(url);
        const routablePath = this.#stripBasePath(path);
        const resolved = this.#resolve(routablePath, search);

        if (!resolved) {
            this.#showNotFound(url);
            return;
        }

        const {route, context, cacheKey} = resolved;
        const cached = route.alwaysReload ? undefined : this.#cache.get(cacheKey);
        if (cached) {
            this.#show(cached, context, navigationId);
            return;
        }

        let view: Component<ComponentEvents> | Promise<Component<ComponentEvents>>;
        try {
            view = route.factory(context);
        } catch (error) {
            this.#showError(url, error);
            return;
        }

        if (!(view instanceof Promise)) {
            if (!route.alwaysReload) {
                this.#cache.set(cacheKey, view);
            }
            this.#show(view, context, navigationId);
            return;
        }

        this.#showLoading(navigationId);
        try {
            const loaded = await view;
            // A newer navigation started while this route was loading.
            if (navigationId !== this.#navigationId) {
                loaded.destroy();
                return;
            }
            if (!route.alwaysReload) {
                this.#cache.set(cacheKey, loaded);
            }
            this.#show(loaded, context, navigationId);
        } catch (error) {
            if (navigationId === this.#navigationId) {
                this.#showError(url, error);
            }
        }
    }

    #resolve(path: string, search: string): { route: Route; context: RouteContext; cacheKey: string } | undefined {
        for (const route of this.#routes) {
            const match = route.pattern.match(path);
            if (!match) {
                continue;
            }
            return {
                route,
                cacheKey: `${route.pattern.source}::${match.consumed}`,
                context: {
                    url: path + search,
                    params: match.params,
                    query: new URLSearchParams(search),
                    remaining: match.remaining
                }
            };
        }
        return undefined;
    }

    #stripBasePath(path: string): string {
        const basePath = this.#options.basePath;
        if (!basePath || !path.startsWith(basePath)) {
            return path;
        }
        return path.slice(basePath.length) || "/";
    }

    #show(view: Component<ComponentEvents>, context: RouteContext, navigationId: number): void {
        if (navigationId !== this.#navigationId) {
            return;
        }
        this.#attach(view);
        if (isNestedRoutable(view)) {
            void view.navigateNested(context.remaining);
        }
        this.trigger("navigate", context);
        this.trigger("afterNavigation", context);
    }

    #showLoading(navigationId: number): void {
        const loading = this.#options.loading?.();
        if (loading && navigationId === this.#navigationId) {
            this.#attach(loading);
        }
    }

    #showNotFound(url: string): void {
        const notFound = this.#options.notFound?.();
        if (notFound) {
            this.#attach(notFound);
            return;
        }
        const fallbackUrl = this.#options.fallbackUrl;
        if (fallbackUrl !== undefined && fallbackUrl !== url) {
            void this.navigateTo(fallbackUrl, {replace: true});
        }
    }

    #showError(url: string, error: unknown): void {
        this.trigger("navigationError", {url, error});
        const errorPage = this.#options.error?.(error);
        if (errorPage) {
            this.#attach(errorPage);
        }
    }

    /** Detaches the previous view without destroying it, so cached views can be shown again. */
    #attach(view: Component<ComponentEvents>): void {
        if (this.#current === view) {
            return;
        }
        if (this.#current) {
            this.removeChild(this.#current);
            if (!this.#isCached(this.#current)) {
                this.#current.destroy();
            }
        }
        this.#current = view;
        this.appendChild(view);
    }

    #isCached(view: Component<ComponentEvents>): boolean {
        for (const cached of this.#cache.values()) {
            if (cached === view) {
                return true;
            }
        }
        return false;
    }
}

const splitUrl = (url: string): [string, string] => {
    const index = url.indexOf("?");
    return index < 0 ? [url, ""] : [url.slice(0, index), url.slice(index)];
};
